// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { SxProps, Theme } from '@mui/material/styles';
import CHATBOT_DRAWER_WIDTH from 'Features/chatbot/constants';

// chatbotOpen shifts the panel + launcher left by the Khora drawer width so they sit beside the open
// chat instead of hidden under it (the shell shifts the rest of the page the same way).
const styles = (theme: Theme, chatbotOpen = false): Record<string, SxProps<Theme>> => ({
  panel: {
    position: 'fixed',
    // Anchored bottom-right, growing upward; raised above the viewer's "Show panos" pill (bottom: 20px)
    // so it stays clear of it. The base right offset (spacing 8 = 64px) keeps the panel LEFT of the
    // map's top-right widget column (home / reset-orientation / zoom, ~48px wide) instead of over it.
    // When Khora is open it slides further left by the drawer width so the chat doesn't cover it.
    bottom: theme.spacing(8),
    right: chatbotOpen ? `calc(${CHATBOT_DRAWER_WIDTH} + ${theme.spacing(8)})` : theme.spacing(8),
    transition: 'right 0.2s ease',
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: '80vh',
    // The panel itself no longer scrolls; the `head` (title + "Current view only") stays pinned and
    // the `body` below it scrolls.
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 2,
    backgroundColor: 'rgba(21, 59, 90, 0.92)', // AirWorks Midnight Blue (#153B5A), translucent over the map
    backdropFilter: 'blur(4px)',
    boxShadow: theme.shadows[6],
    color: theme.palette.common.white,
    zIndex: 6,
  },
  // Collapsed state: a compact circular button in the same top-right corner as the panel,
  // so hiding the dashboard leaves a tidy affordance instead of an empty full-width box.
  launcher: {
    position: 'fixed',
    // Bottom-right corner, tucked to the right of the viewer's "Show panos" pill (bottom: 20px,
    // a center-right pill); the chatbot launcher is raised just above. Nudge bottom/right here if
    // it doesn't line up against the actual pill width.
    bottom: theme.spacing(2.5),
    right: chatbotOpen ? `calc(${CHATBOT_DRAWER_WIDTH} + ${theme.spacing(1)})` : theme.spacing(1),
    transition: 'right 0.2s ease',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: 'rgba(21, 59, 90, 0.92)', // AirWorks Midnight Blue (#153B5A), translucent over the map
    backdropFilter: 'blur(4px)',
    boxShadow: theme.shadows[6],
    color: theme.palette.common.white,
    zIndex: 11, // above the map + the Show-panos pill (z-index 10), reliably clickable
    '&:hover': { backgroundColor: 'rgba(40, 70, 96, 0.96)' },
  },
  // Pinned top of the panel — the "Dashboard" title + the "Current view only" toggle stay put while
  // the body scrolls beneath them.
  head: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  // Scrolling region below the pinned head (min-height:0 lets it shrink so the panel's maxHeight wins).
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.palette.common.white,
  },
  // Layer focus dropdown, themed for the dark panel.
  layerSelect: {
    color: theme.palette.common.white,
    fontSize: '0.8rem',
    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.25)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
    '.MuiSvgIcon-root': { color: theme.palette.common.white },
  },
  layerMenu: {
    maxHeight: 320,
    backgroundColor: 'rgba(21, 59, 90, 0.98)', // AirWorks Midnight Blue (#153B5A)
    color: theme.palette.common.white,
    '.MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
    '.MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(1, 161, 221, 0.28)' }, // AirWorks blue tint
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
  },
  card: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    minWidth: 0, // let grid cells shrink so long values wrap instead of overflowing
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: '1.2rem',
    overflowWrap: 'anywhere',
    // Typography defaults to text.primary (dark) — set white so the value contrasts on the dark panel.
    color: theme.palette.common.white,
  },
  kpiHelp: {
    color: theme.palette.grey[400],
    fontSize: '0.7rem',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  unpin: {
    color: theme.palette.grey[400],
    padding: 0,
  },
  chartCard: {
    padding: theme.spacing(1, 0.5, 0.5, 0.5),
    borderRadius: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    // Clicking a chart element (esp. a pie sector) focuses the SVG path -> a rectangular browser
    // focus outline around it. Suppress it so the selection follows the slice, not a box.
    '& :focus, & :focus-visible': { outline: 'none' },
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: '0.8rem',
    paddingLeft: theme.spacing(1),
    // Typography defaults to text.primary (near-black) — set white so the heading reads on the dark card.
    color: theme.palette.common.white,
  },
  // Per-chart tool buttons (data table + "how it's derived") on the right of the card header.
  cardTools: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  cardToolBtn: {
    color: theme.palette.grey[400],
    padding: theme.spacing(0.25),
    '&:hover': { color: theme.palette.common.white },
  },
  // 2D-histogram / heatmap grid: row labels + a matrix of brand-blue cells shaded by count.
  heatmapWrap: {
    padding: theme.spacing(0.5, 1, 1),
  },
  heatmapGrid: {
    display: 'grid',
    gap: '2px',
    alignItems: 'stretch',
  },
  heatmapColLabel: {
    fontSize: '0.58rem',
    color: theme.palette.grey[400],
    textAlign: 'center',
    alignSelf: 'end',
    whiteSpace: 'nowrap',
  },
  heatmapRowLabel: {
    fontSize: '0.62rem',
    color: '#dfe7ee',
    paddingRight: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  heatmapCell: {
    minHeight: 26,
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.66rem',
    fontWeight: 600,
  },
  heatmapAxis: {
    marginTop: theme.spacing(0.75),
    fontSize: '0.6rem',
    color: theme.palette.grey[500],
    textAlign: 'center',
  },
  // "How this chart is derived" explanation, shown under the chart when the info button is on.
  methodPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(0.5, 1, 0),
    padding: theme.spacing(0.75, 1),
    borderRadius: 1,
    backgroundColor: 'rgba(1, 161, 221, 0.12)', // AirWorks blue tint
    borderLeft: '2px solid rgba(1, 161, 221, 0.6)',
  },
  methodLine: {
    color: '#dfe7ee',
    fontSize: '0.72rem',
    lineHeight: 1.35,
  },
  gaugeWrap: {
    position: 'relative',
  },
  // Centered over the radial gauge (recharts won't reliably render raw <text> children).
  gaugeValue: {
    position: 'absolute',
    top: '52%',
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.4rem',
    color: theme.palette.common.white,
    pointerEvents: 'none',
  },
  // "Interesting facts" list: one label -> value row per fact, separated by faint dividers.
  factsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  factRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
  },
  factLabel: {
    color: theme.palette.grey[400],
    fontSize: '0.72rem',
    whiteSpace: 'nowrap',
  },
  factValue: {
    color: '#eee',
    fontSize: '0.78rem',
    fontWeight: 600,
    textAlign: 'right',
    overflowWrap: 'anywhere',
  },
  // Wide tables scroll horizontally inside their card instead of overflowing the panel.
  tableWrap: {
    overflowX: 'auto',
    // Scroll a long table (full clearance punch-list / layer inventory) inside the card instead of
    // capping the rows — never drop data, paginate the delivery.
    overflowY: 'auto',
    maxHeight: 320,
    maxWidth: '100%',
  },
  thCell: {
    color: theme.palette.grey[400],
    borderColor: 'rgba(255, 255, 255, 0.1)',
    whiteSpace: 'nowrap',
    fontSize: '0.72rem',
  },
  tdCell: {
    color: '#eee',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    whiteSpace: 'nowrap',
    fontSize: '0.72rem',
  },
  empty: {
    color: theme.palette.grey[400],
    textAlign: 'center',
    padding: theme.spacing(2),
  },
});

export default styles;
